// src/components/ValidateExpertButton.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Award, Star, Check } from "lucide-react";
import { toast } from "react-toastify";

/**
 * Composant pour valider un expert
 * Version simplifiée sans Edge Functions
 */
export default function ValidateExpertButton({ expertId, expertName, onValidationChange }) {
  const [hasValidated, setHasValidated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [rating, setRating] = useState(0);
  const [validationNote, setValidationNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkIfAlreadyValidated();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertId]);

  const checkIfAlreadyValidated = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return;

      const { data, error } = await supabase
        .from("expert_validations")
        .select("id")
        .eq("expert_id", expertId)
        .eq("validator_client_id", user.id)
        .maybeSingle();

      if (error) throw error;
      setHasValidated(!!data);
    } catch (e) {
      console.error("[validate] check error:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    if (hasValidated) {
      if (!window.confirm("Retirer votre validation de cet expert ?")) {
        return;
      }
      await revokeValidation();
    } else {
      setShowRatingModal(true);
    }
  };

  const submitValidation = async () => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error("Non authentifié");
      }

      const { error } = await supabase
        .from("expert_validations")
        .insert({
          expert_id: expertId,
          validator_client_id: user.id,
          rating: rating > 0 ? rating : null,
          validation_note: validationNote.trim() || null,
        });

      if (error) throw error;

      toast.success("Expert validé avec succès !");
      setHasValidated(true);
      setShowRatingModal(false);
      setRating(0);
      setValidationNote("");
      
      if (onValidationChange) {
        onValidationChange(true);
      }
    } catch (e) {
      console.error("[validate] submit error:", e);
      toast.error(e?.message || "Erreur lors de la validation");
    } finally {
      setSubmitting(false);
    }
  };

  const revokeValidation = async () => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error("Non authentifié");
      }

      const { error } = await supabase
        .from("expert_validations")
        .delete()
        .eq("expert_id", expertId)
        .eq("validator_client_id", user.id);

      if (error) throw error;

      toast.success("Validation retirée");
      setHasValidated(false);
      
      if (onValidationChange) {
        onValidationChange(false);
      }
    } catch (e) {
      console.error("[validate] revoke error:", e);
      toast.error(e?.message || "Erreur lors du retrait de la validation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: "8px 12px",
        fontSize: 12,
        color: "#9ca3af",
        fontFamily: "system-ui, sans-serif"
      }}>
        Chargement...
      </div>
    );
  }

  return (
    <>
      <button
        onClick={handleValidate}
        disabled={submitting}
        style={{
          padding: "8px 14px",
          background: hasValidated ? "#ecfdf5" : "#fffef7",
          color: hasValidated ? "#065f46" : "#92400e",
          border: hasValidated ? "1px solid #d1fae5" : "1px solid #f5e6d3",
          borderRadius: 2,
          cursor: submitting ? "not-allowed" : "pointer",
          fontFamily: "system-ui, sans-serif",
          fontSize: 12,
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
          gap: 6,
          transition: "all 0.2s ease",
          opacity: submitting ? 0.6 : 1
        }}
      >
        {hasValidated ? (
          <>
            <Check size={14} strokeWidth={2} />
            Expert validé
          </>
        ) : (
          <>
            <Award size={14} />
            Valider cet expert
          </>
        )}
      </button>

      {showRatingModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20
          }}
          onClick={() => !submitting && setShowRatingModal(false)}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 2,
              padding: 32,
              maxWidth: 480,
              width: "100%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              fontFamily: "Georgia, serif",
              fontSize: 24,
              fontWeight: 400,
              color: "#1a1a1a",
              marginBottom: 8,
              letterSpacing: "-0.02em"
            }}>
              Valider {expertName}
            </div>
            
            <div style={{
              fontFamily: "system-ui, sans-serif",
              fontSize: 14,
              color: "#6b6b6b",
              lineHeight: 1.6,
              marginBottom: 24
            }}>
              Votre validation aide d'autres collectionneurs à identifier les experts de confiance.
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{
                fontFamily: "system-ui, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#1a1a1a",
                marginBottom: 10
              }}>
                Note (optionnel)
              </div>
              <div style={{
                display: "flex",
                gap: 8,
                alignItems: "center"
              }}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={28}
                    style={{ cursor: "pointer" }}
                    color={star <= rating ? "#d4af37" : "#e5e3df"}
                    fill={star <= rating ? "#d4af37" : "none"}
                    strokeWidth={1.5}
                    onClick={() => setRating(star)}
                  />
                ))}
                {rating > 0 && (
                  <button
                    onClick={() => setRating(0)}
                    style={{
                      marginLeft: 8,
                      padding: "4px 8px",
                      background: "transparent",
                      border: "1px solid #e5e3df",
                      borderRadius: 2,
                      fontSize: 11,
                      cursor: "pointer",
                      fontFamily: "system-ui, sans-serif",
                      color: "#6b6b6b"
                    }}
                  >
                    Réinitialiser
                  </button>
                )}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: "block",
                fontFamily: "system-ui, sans-serif",
                fontSize: 13,
                fontWeight: 600,
                color: "#1a1a1a",
                marginBottom: 8
              }}>
                Commentaire (optionnel)
              </label>
              <textarea
                value={validationNote}
                onChange={(e) => setValidationNote(e.target.value)}
                placeholder="Décrivez brièvement votre expérience avec cet expert..."
                maxLength={500}
                rows={4}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #e5e3df",
                  borderRadius: 2,
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 13,
                  resize: "vertical"
                }}
              />
              <div style={{
                fontSize: 11,
                color: "#9ca3af",
                marginTop: 4,
                fontFamily: "system-ui, sans-serif"
              }}>
                {validationNote.length}/500 caractères
              </div>
            </div>

            <div style={{
              display: "flex",
              gap: 12,
              justifyContent: "flex-end"
            }}>
              <button
                onClick={() => !submitting && setShowRatingModal(false)}
                disabled={submitting}
                style={{
                  padding: "12px 20px",
                  background: "#fff",
                  color: "#1a1a1a",
                  border: "1px solid #e5e3df",
                  borderRadius: 2,
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  opacity: submitting ? 0.5 : 1
                }}
              >
                Annuler
              </button>
              <button
                onClick={submitValidation}
                disabled={submitting}
                style={{
                  padding: "12px 20px",
                  background: "#2c5f2d",
                  color: "#fff",
                  border: "none",
                  borderRadius: 2,
                  cursor: submitting ? "not-allowed" : "pointer",
                  fontFamily: "system-ui, sans-serif",
                  fontSize: 13,
                  fontWeight: 500,
                  opacity: submitting ? 0.7 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 8
                }}
              >
                {submitting ? (
                  "Validation en cours..."
                ) : (
                  <>
                    <Award size={16} />
                    Valider l'expert
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}